/**
 * Talent Profile Detail — council/admin read-only view of a single talent.
 *
 * Different from `/profile` (own-profile editor). Opens when a council
 * member clicks a card in `/talents`. The route is `/talents/:id`.
 *
 * Visual alignment: mockup §2428-2573 (talent-card) expanded to full page.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeft20Regular, PersonAdd20Regular } from '@fluentui/react-icons'
import api from '../lib/api'
import { FOCUS_AREAS } from '../types/focus_area'
import { bayerColors } from '../theme'
import { apiErrorMessage } from '../lib/apiError'
import { activatable } from '../lib/activatable'
import { APPLICATION_STATUS_LABELS } from '../lib/labels'
import type { ApplicationStatus } from '../types/application'

interface TalentDetail {
  id: number
  name: string
  email: string
  role: string
  focus_area_code: string | null
  focus_area_role: string | null
  bio: string | null
  skills: string[]
  visible_in_directory: boolean
  is_test_account: boolean
  active_demand_count: number
  completed_demand_count: number
  // Self-declared links
  github_handle: string | null
  // MS Graph enrichment
  job_title: string | null
  department: string | null
  office_location: string | null
  company: string | null
  country: string | null
  // Application history (scoped to viewer's FA)
  application_history?: ApplicationHistoryItem[]
}

interface ApplicationHistoryItem {
  id: number
  demand_id: number
  demand_title: string | null
  demand_code: string | null
  demand_stage: string | null
  focus_area_code: string | null
  status: ApplicationStatus
  feedback: string | null
  proposed_hours_per_week: number | null
  proposed_duration: string | null
  created_at: string | null
}

const BRAND_PRIMARY = bayerColors.navyMid

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '780px',
  },
  back: { alignSelf: 'flex-start' },

  /* Hero card */
  hero: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'flex-start',
    padding: '24px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow4,
  },
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${BRAND_PRIMARY}, #1a4a75)`,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '22px',
    flexShrink: 0,
  },
  heroInfo: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 },
  heroName: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  heroMeta: { fontSize: '13px', color: tokens.colorNeutralForeground3 },
  heroEmail: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  testBadge: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    background: '#e65100',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  faTag: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '3px',
    background: bayerColors.navyLight,
    color: BRAND_PRIMARY,
  },
  inviteBtn: { alignSelf: 'flex-start', flexShrink: 0 },

  /* Stats bar */
  stats: {
    display: 'flex',
    gap: '24px',
    padding: '16px 24px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  statValue: { fontSize: '20px', fontWeight: 700, color: tokens.colorNeutralForeground1 },
  statLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },

  /* Sections */
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: '20px 24px',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  bio: {
    whiteSpace: 'pre-wrap' as const,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.6',
    fontSize: '14px',
  },

  /* Skill chips — matches mockup .skill-chip */
  chips: { display: 'flex', flexWrap: 'wrap' as const, gap: '6px' },
  chip: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '3px 10px',
    background: bayerColors.navyLight,
    color: BRAND_PRIMARY,
    borderRadius: '12px',
    lineHeight: '16px',
  },
  emptyHint: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic' as const,
  },

  /* Application history table */
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  historyTh: {
    textAlign: 'left' as const,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  historyTd: {
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: 'middle' as const,
    color: tokens.colorNeutralForeground1,
  },
  historyLink: {
    color: BRAND_PRIMARY,
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  demandCodeChip: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  statusBadge: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    display: 'inline-block',
  },
  statusApproved: { background: '#dff6dd', color: '#0e700e' },
  statusRejected: { background: '#fde7e9', color: '#c4314b' },
  statusPending: { background: bayerColors.navyLight, color: BRAND_PRIMARY },
  statusWithdrawn: { background: '#f0f0f0', color: '#616161' },
  /* Directory info grid */
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px 24px',
  },
  infoItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  infoLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  infoValue: { fontSize: '14px', color: tokens.colorNeutralForeground1 },
})

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

export function TalentProfileDetail() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  // Keyed by status rather than chained ternaries: the chain's default branch
  // rendered a withdrawn application as "Pending", so council saw a live
  // candidate where there was none.
  const statusClass: Record<ApplicationStatus, string> = {
    pending: styles.statusPending,
    approved: styles.statusApproved,
    rejected: styles.statusRejected,
    withdrawn: styles.statusWithdrawn,
  }

  const { data: t, isLoading, isError, error } = useQuery<TalentDetail>({
    queryKey: ['talent-detail', id],
    queryFn: async () => {
      const res = await api.get<TalentDetail>(`/users/talents/${id}`)
      return res.data
    },
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading talent..." />
      </div>
    )
  }
  if (isError || !t) {
    return (
      <div className={styles.page}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => {
            if (window.history.length > 1) navigate(-1)
            else navigate('/talents')
          }}
          className={styles.back}
        >
          Back
        </Button>
        {/* Was "They may have been removed." — a CAUSE this branch never
            checked. `isError` covers a 403, a 500 and a dropped connection just
            as readily as a 404, so the one guess it offered was wrong more
            often than right, and it sent the reader looking for a deletion that
            had not happened. The server's own words instead. */}
        <p>Could not load this talent. {apiErrorMessage(error)}</p>
      </div>
    )
  }

  const isTest = t.is_test_account || t.name.startsWith('[TEST]')
  const faMeta = FOCUS_AREAS.find((f) => f.code === t.focus_area_code)

  return (
    <div className={styles.page}>
      <Button
        appearance="subtle"
        icon={<ArrowLeft20Regular />}
        onClick={() => {
          if (window.history.length > 1) navigate(-1)
          else navigate('/talents')
        }}
        className={styles.back}
      >
        Back
      </Button>

      {/* Hero card */}
      <header className={styles.hero}>
        <div className={styles.avatar}>{initials(t.name)}</div>
        <div className={styles.heroInfo}>
          <h1 className={styles.heroName}>
            {t.name}
            {isTest && <span className={styles.testBadge}>TEST</span>}
          </h1>
          <div className={styles.heroMeta}>
            {t.role}
            {t.focus_area_code && (
              <>
                {' \u00b7 '}
                <span className={styles.faTag}>{t.focus_area_code}</span>
                {faMeta && ` ${faMeta.name}`}
                <span style={{ opacity: 0.65 }}> (preferred)</span>
              </>
            )}
          </div>
          <div className={styles.heroEmail}>{t.email}</div>
        </div>
        <Button
          appearance="primary"
          icon={<PersonAdd20Regular />}
          className={styles.inviteBtn}
          style={{ background: BRAND_PRIMARY, borderColor: BRAND_PRIMARY }}
          onClick={() => navigate(`/demands?invite_talents=${t.id}`)}
        >
          Invite to demand
        </Button>
      </header>

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{t.completed_demand_count}</span>
          <span className={styles.statLabel}>Completed</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{t.active_demand_count}</span>
          <span className={styles.statLabel}>Active</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{t.skills.length}</span>
          <span className={styles.statLabel}>Skills</span>
        </div>
      </div>

      {/* Organization (from MS directory) */}
      {(t.job_title || t.department || t.company || t.country || t.office_location) && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Organization</h2>
          <div className={styles.infoGrid}>
            {t.job_title && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Job title</span>
                <span className={styles.infoValue}>{t.job_title}</span>
              </div>
            )}
            {t.department && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Department</span>
                <span className={styles.infoValue}>{t.department}</span>
              </div>
            )}
            {t.company && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Company</span>
                <span className={styles.infoValue}>{t.company}</span>
              </div>
            )}
            {t.country && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Country</span>
                <span className={styles.infoValue}>{t.country}</span>
              </div>
            )}
            {t.office_location && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Office</span>
                <span className={styles.infoValue}>{t.office_location}</span>
              </div>
            )}
            {t.github_handle && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>GitHub</span>
                <span className={styles.infoValue}>
                  <a
                    href={`https://github.com/${t.github_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    @{t.github_handle}
                  </a>
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Bio */}
      {t.bio && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>About</h2>
          <p className={styles.bio}>{t.bio}</p>
        </section>
      )}

      {/* Skills */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Skills &amp; Interests</h2>
        {t.skills.length === 0 ? (
          <p className={styles.emptyHint}>This talent hasn't listed any skills yet.</p>
        ) : (
          <div className={styles.chips}>
            {t.skills.map((s) => (
              <span key={s} className={styles.chip}>
                {s}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Application History (scoped to viewer's FA) */}
      {t.application_history && t.application_history.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Application History</h2>
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th className={styles.historyTh}>Demand</th>
                <th className={styles.historyTh}>Status</th>
                <th className={styles.historyTh}>Hours/wk</th>
                <th className={styles.historyTh}>Applied</th>
              </tr>
            </thead>
            <tbody>
              {t.application_history.map((app) => (
                <tr key={app.id}>
                  <td className={styles.historyTd}>
                    <span
                      className={styles.historyLink}
                      role="link"
                      {...activatable(() => navigate(`/demand/${app.demand_id}`))}
                    >
                      {app.demand_code && (
                        <span className={styles.demandCodeChip}>{app.demand_code}</span>
                      )}
                      {app.demand_title || `#${app.demand_id}`}
                    </span>
                  </td>
                  <td className={styles.historyTd}>
                    <span className={`${styles.statusBadge} ${statusClass[app.status] ?? styles.statusPending}`}>
                      {APPLICATION_STATUS_LABELS[app.status] ?? app.status}
                    </span>
                  </td>
                  <td className={styles.historyTd}>
                    {app.proposed_hours_per_week ? `${app.proposed_hours_per_week}h` : '—'}
                  </td>
                  <td className={styles.historyTd}>
                    {app.created_at
                      ? new Date(app.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {t.application_history && t.application_history.length === 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Application History</h2>
          <p className={styles.emptyHint}>No applications within your focus area.</p>
        </section>
      )}
    </div>
  )
}

export default TalentProfileDetail
